/**
 * Document construction — the synthetic style table, the empty document, the cue and run
 * builders every parser and the ASR bridge need.
 *
 * This exists because `documentFromTranscript` is contractually required to produce "a
 * COMPLETE document — synthetic style table, one track, transcript retained … so nobody has
 * to invent a synthetic style table four ways". Eleven parsers would otherwise each invent
 * their own, and the eleventh would differ.
 *
 * Everything here is a pure function returning frozen-shaped plain JSON. No mutation, ever.
 */

import type {
  Alignment,
  Color,
  Cue,
  CueFlags,
  CueKind,
  CueOrigin,
  DocumentId,
  DocumentMeta,
  Framerate,
  IdAllocator,
  InputId,
  InputRef,
  ReadFormat,
  Resolution,
  Style,
  StyleId,
  StyleTable,
  SubtitleDocument,
  TextLine,
  TextRun,
  TickDelta,
  TimeSpan,
  Ticks,
  Timebase,
  Track,
  TrackId,
} from '../../../contracts/cue-model.ts';
import { SCHEMA_VERSION } from '../../../contracts/cue-model.ts';
import { T } from '../time/ticks.ts';

export const WHITE: Color = { r: 255, g: 255, b: 255, a: 1 };
export const BLACK: Color = { r: 0, g: 0, b: 0, a: 1 };
export const RED: Color = { r: 255, g: 0, b: 0, a: 1 };

/**
 * libass's legacy fallback. `provenance: 'assumed'` is what makes positions advisory and
 * diagnosable rather than silently authoritative.
 */
export const ASSUMED_RESOLUTION: Resolution = { x: 384, y: 288, provenance: 'assumed' };

/** A timebase that claims nothing: no framerate, nothing frame-native, no lattice. */
export const UNKNOWN_TIMEBASE: Timebase = {
  framerate: null,
  framerateSource: null,
  frameNative: false,
  grid: null,
  timecodeOffset: T.delta(0),
};

/** Millisecond grid — what an SRT/VTT/SBV parse produces. */
export const MS_GRID: Framerate = { num: 1000, den: 1, label: 'ms' };
/** Centisecond grid — what an ASS/SSA/LRC parse produces. */
export const CS_GRID: Framerate = { num: 100, den: 1, label: 'cs' };
/** Decisecond grid — MPL2. */
export const DS_GRID: Framerate = { num: 10, den: 1, label: 'ds' };

/**
 * The synthetic style's font size, exported because a parser that builds a style from a
 * partial declaration (a SAMI class with no `font-size`) must fall back to the SAME number:
 * `project()` inlines whatever a named style changes against the synthetic one, so a
 * fallback that differs reads as a size change and lands on every cue.
 */
export const SYNTHETIC_FONT_SIZE = 16;

/**
 * The style every format that has no style table gets. `isSynthetic: true` is what tells a
 * writer it need not be emitted, so an SRT→SRT round trip does not sprout a [V4+ Styles].
 *
 * THE GEOMETRY IS ffmpeg's SRT→ASS DEFAULT (Fontsize 16, Outline 1, Shadow 0, margins 10),
 * because these numbers are pixels against `ASSUMED_RESOLUTION`'s PlayResY 288, the
 * resolution every document without one of its own gets. The previous 48 / Outline 2 is
 * Aegisub's default style, which assumes a much taller script. Measured with ffmpeg 9.0.1's
 * libass on a 1280×720 frame: at 48 a 42-character line wrapped to two lines and stood 214 px
 * tall (30% of the frame; a single short line was 100 px, 14%). At 16 the same line stays on
 * one line, 34 px tall (4.7%) and 605 px wide (47%), pixel-identical to `ffmpeg -i x.srt
 * x.ass`. Outline is in the same pixels, so it scales down with the font (2 px on a 16 px
 * glyph is twice ffmpeg's weight); the margins are the ASS convention at this PlayRes and stay.
 */
export function syntheticStyle(id: StyleId, name = 'Default'): Style {
  return {
    id,
    name,
    fontName: 'Arial',
    fontSize: SYNTHETIC_FONT_SIZE,
    primaryColor: WHITE,
    secondaryColor: RED,
    outlineColor: BLACK,
    shadowColor: BLACK,
    bold: false,
    italic: false,
    underline: false,
    strikeOut: false,
    scaleX: 100,
    scaleY: 100,
    spacing: 0,
    angle: 0,
    borderStyle: 'outline',
    outlineWidth: 1,
    shadowDepth: 0,
    alignment: 2 satisfies Alignment,
    margins: { left: 10, right: 10, vertical: 10 },
    encoding: 1,
    rawColumns: {},
    isSynthetic: true,
  };
}

export function singleStyleTable(style: Style): StyleTable {
  return { byId: { [style.id]: style }, order: [style.id], defaultId: style.id };
}

export const NO_FLAGS: CueFlags = {
  forced: false,
  autoGenerated: false,
  locked: false,
  hasFragileTags: false,
  hasRelativeTimes: false,
};

export function originFor(
  inputId: InputId,
  op: CueOrigin['op'],
  index: number | null = null,
): CueOrigin {
  return { inputId, index, sourceLine: null, from: [], op, segmentIds: [], wordRange: null };
}

export interface RunInit {
  readonly id: TextRun['id'];
  readonly text: string;
  readonly time?: TimeSpan | null;
  readonly timeKind?: TextRun['timeKind'];
  readonly timeOrigin?: TextRun['timeOrigin'];
  readonly confidence?: number | null;
  readonly startsWord?: boolean;
  readonly style?: TextRun['style'];
  readonly kind?: TextRun['kind'];
  readonly drawing?: TextRun['drawing'];
  readonly annotation?: TextRun['annotation'];
  readonly sourceOffset?: number | null;
}

/** A TextRun with every optional field defaulted. `timeKind`/`timeOrigin` are null exactly
 *  when `time` is null, which is a document invariant, so it is enforced here at birth. */
export function makeRun(init: RunInit): TextRun {
  const time = init.time ?? null;
  return {
    id: init.id,
    kind: init.kind ?? 'text',
    text: init.kind === 'drawing' ? '' : init.text,
    time,
    timeKind: time === null ? null : (init.timeKind ?? 'word'),
    timeOrigin: time === null ? null : (init.timeOrigin ?? 'file'),
    confidence: init.confidence ?? null,
    startsWord: init.startsWord ?? true,
    style: init.style ?? {},
    annotation: init.annotation ?? null,
    drawing: init.drawing ?? null,
    sourceOffset: init.sourceOffset ?? null,
  };
}

export function makeLine(
  runs: readonly TextRun[],
  init: Partial<Omit<TextLine, 'runs'>> = {},
): TextLine {
  return {
    runs,
    breakBefore: init.breakBefore ?? 'none',
    align: init.align ?? null,
    trackId: init.trackId ?? null,
    inputId: init.inputId ?? null,
  };
}

export interface CueInit {
  readonly id: Cue['id'];
  readonly time: TimeSpan;
  readonly lines: readonly TextLine[];
  readonly styleId: StyleId;
  readonly trackId: TrackId;
  readonly origin: CueOrigin;
  readonly kind?: CueKind;
  readonly index?: number | null;
  readonly layer?: number;
  readonly granularity?: Cue['granularity'];
  readonly flags?: CueFlags;
  readonly rev?: number;
}

export function makeCue(init: CueInit): Cue {
  return {
    id: init.id,
    rev: init.rev ?? 0,
    trackId: init.trackId,
    kind: init.kind ?? 'dialogue',
    commented: false,
    index: init.index ?? null,
    time: init.time,
    frames: null,
    layer: init.layer ?? 0,
    styleId: init.styleId,
    actor: null,
    effect: null,
    margins: null,
    position: null,
    fade: null,
    region: null,
    wrap: null,
    karaoke: null,
    lines: init.lines,
    granularity: init.granularity ?? 'coalesced',
    extra: {},
    flags: init.flags ?? NO_FLAGS,
    origin: init.origin,
    raw: null,
  };
}

export function span(start: number, end: number): TimeSpan {
  return { start: T.ticks(start), end: T.ticks(end) };
}

export interface DocumentInit {
  readonly id: DocumentId;
  readonly cues?: readonly Cue[];
  readonly styles: StyleTable;
  readonly tracks: readonly Track[];
  readonly inputs: readonly InputRef[];
  readonly meta: DocumentMeta;
}

export function makeDocument(init: DocumentInit): SubtitleDocument {
  return {
    schema: SCHEMA_VERSION,
    id: init.id,
    cues: init.cues ?? [],
    styles: init.styles,
    tracks: init.tracks,
    regions: [],
    inputs: init.inputs,
    meta: init.meta,
    transcripts: [],
    ass: null,
    loss: [],
    diagnostics: [],
  };
}

export interface MetaInit {
  readonly title?: string | null;
  readonly sourceFormat?: ReadFormat | null;
  readonly resolution?: Resolution;
  readonly timebase?: Timebase;
  readonly durationTicks?: Ticks | null;
  readonly producedBy?: DocumentMeta['producedBy'];
  readonly parents?: readonly DocumentId[];
  readonly extra?: readonly (readonly [string, string])[];
}

export function makeMeta(init: MetaInit = {}): DocumentMeta {
  return {
    title: init.title ?? null,
    sourceFormat: init.sourceFormat ?? null,
    resolution: init.resolution ?? ASSUMED_RESOLUTION,
    timebase: init.timebase ?? UNKNOWN_TIMEBASE,
    wrapStyle: 'smart-top',
    scaledBorderAndShadow: true,
    collisions: 'normal',
    durationTicks: init.durationTicks ?? null,
    producedBy: init.producedBy ?? 'parse',
    parents: init.parents ?? [],
    extra: init.extra ?? [],
  };
}

export function makeTrack(
  id: TrackId,
  order = 0,
  init: Partial<Omit<Track, 'id' | 'order'>> = {},
): Track {
  return {
    id,
    name: init.name ?? 'default',
    language: init.language ?? null,
    kind: init.kind ?? 'subtitle',
    order,
  };
}

export function makeInput(
  id: InputId,
  name: string,
  order = 0,
  init: Partial<Omit<InputRef, 'id' | 'name' | 'order'>> = {},
): InputRef {
  return {
    id,
    name,
    format: init.format ?? null,
    cueCount: init.cueCount ?? 0,
    encoding: init.encoding ?? null,
    byteLength: init.byteLength ?? null,
    containerTrackIndex: init.containerTrackIndex ?? null,
    order,
    resolution: init.resolution ?? null,
  };
}

/**
 * A complete, valid, empty single-track document. The thing every parser starts from and
 * every test needs. Mints ids through the allocator, never by brand-casting a literal.
 */
export function emptyDocument(
  alloc: IdAllocator,
  opts: {
    readonly inputName: string;
    readonly sourceFormat?: ReadFormat | null;
    readonly language?: string | null;
    readonly timebase?: Timebase;
    readonly title?: string | null;
    readonly durationTicks?: Ticks | null;
    readonly producedBy?: DocumentMeta['producedBy'];
    readonly resolution?: Resolution;
  },
): {
  readonly doc: SubtitleDocument;
  readonly inputId: InputId;
  readonly trackId: TrackId;
  readonly styleId: StyleId;
} {
  const inputId = alloc.input();
  const trackId = alloc.track();
  const styleId = alloc.style();
  const style = syntheticStyle(styleId);
  const doc = makeDocument({
    id: alloc.document(),
    styles: singleStyleTable(style),
    tracks: [makeTrack(trackId, 0, { name: opts.inputName, language: opts.language ?? null })],
    inputs: [makeInput(inputId, opts.inputName, 0, { format: opts.sourceFormat ?? null })],
    meta: makeMeta({
      title: opts.title ?? null,
      sourceFormat: opts.sourceFormat ?? null,
      timebase: opts.timebase ?? UNKNOWN_TIMEBASE,
      durationTicks: opts.durationTicks ?? null,
      ...(opts.resolution === undefined ? {} : { resolution: opts.resolution }),
      producedBy: opts.producedBy ?? 'parse',
    }),
  });
  return { doc, inputId, trackId, styleId };
}

/** Structural-sharing helper: returns `next` only when it differs, else `prev` by reference. */
export function keepIfEqual<T>(prev: T, next: T, equal: (a: T, b: T) => boolean): T {
  return equal(prev, next) ? prev : next;
}

export function sameSpan(a: TimeSpan | null, b: TimeSpan | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  return (a.start as number) === (b.start as number) && (a.end as number) === (b.end as number);
}

export const ZERO_DELTA: TickDelta = T.delta(0);
